import { memo, useContext, useEffect, useMemo, type ReactNode } from 'react'
import { useSelector } from 'react-redux'
import { ChartViewKey } from '../../../../assets/constants'
import {
    selectMultiStrikeChartConfigCallPut,
    selectMultiStrikeChartConfigChartType,
    selectMultiStrikeChartConfigSecurities,
    selectMultiStrikeChartConfigStyles,
} from '../../../../selectors/chart'
import {
    selectMultiStrikeChartCallPut,
    selectMultiStrikeChartSecurities,
    selectMultiStrikeChartStyles,
    selectMultiStrikeChartType,
} from '../../../../selectors/multiStrikeChart'
import { getMultiStrikeCallColor, getMultiStrikePutColor } from '../../multiStrikeColorPalettes'
import { MultiStrikeSecuritiesClassType } from '../../../../reducer/multiStrikeChart.reducer'
import { AppContext } from '../../../../App'
import { Box, useTheme } from '@mui/material'
import MultiStrikeStrikeMetadataRow from './MultiStrikeStrikeMetadataRow'
import SeriesOptionMetadataDisplay from './seriesOptionMetadataDisplay'
import { resolveMultiStrikeColorPalette } from '../../multiStrikeColorPalettes'
import { MULTI_STRIKE_METADATA_TABLE_SX } from './multiStrikeMetadataLayout'

type Props = {
    viewKey: string
    multiStrikeClassType: MultiStrikeSecuritiesClassType
}

const MultiStrikeMetadataStrikeList = ({ viewKey, multiStrikeClassType }: Props) => {
    const appContext = useContext(AppContext)
    const chart = appContext.getMultiStrikeChart(viewKey)
    const theme = useTheme()
    const isWidget = useMemo(() => viewKey !== ChartViewKey.MULTI_STRIKES_CHART, [viewKey])
    const multiStrikeChartSecurities = useSelector(
        isWidget ? selectMultiStrikeChartConfigSecurities(viewKey) : selectMultiStrikeChartSecurities
    )
    const chartType = useSelector(isWidget ? selectMultiStrikeChartConfigChartType(viewKey) : selectMultiStrikeChartType)
    const cumCallPut = useSelector(isWidget ? selectMultiStrikeChartConfigCallPut(viewKey) : selectMultiStrikeChartCallPut)
    const chartStyles = useSelector(isWidget ? selectMultiStrikeChartConfigStyles(viewKey) : selectMultiStrikeChartStyles)
    const themeMode = theme.palette.mode === 'dark' ? 'dark' : 'light'
    const colorPalette = useMemo(
        () => resolveMultiStrikeColorPalette(chartStyles, themeMode),
        [chartStyles, themeMode]
    )

    const strikeRows = useMemo(() => {
        return Object.keys(multiStrikeClassType ?? {}).map((strike, index) => ({
            strike,
            callKey: multiStrikeClassType?.[strike]?.callSecKey,
            putKey: multiStrikeClassType?.[strike]?.putSecKey,
            callSeriesColor: getMultiStrikeCallColor(chartStyles, index, themeMode),
            putSeriesColor: getMultiStrikePutColor(chartStyles, index, themeMode),
            callPut:
                chartType === 'cumOi'
                    ? undefined
                    : multiStrikeChartSecurities?.[strike],
        }))
    }, [chartType, multiStrikeChartSecurities, multiStrikeClassType, chartStyles, themeMode])

    useEffect(() => {
        if (!chart || !multiStrikeChartSecurities || !multiStrikeClassType) return
        const callPutSecurities: MultiStrikeSecuritiesClassType = {}
        Object.keys(multiStrikeChartSecurities).forEach((strike) => {
            callPutSecurities[strike] = {
                callPut: multiStrikeChartSecurities[strike],
                callSecKey: multiStrikeClassType?.[strike]?.callSecKey,
                putSecKey: multiStrikeClassType?.[strike]?.putSecKey,
            }
        })
        if (chartType === 'cumOi') {
            chart.updateCumOiCallPutVisible()
        } else {
            chart.updateStrikeCallPutVisible(callPutSecurities)
        }
    }, [chart, multiStrikeChartSecurities, multiStrikeClassType, chartType])

    const renderTableBody = (rows: ReactNode) => (
        <Box component="table" sx={MULTI_STRIKE_METADATA_TABLE_SX} className="font-feature-num">
            <Box component="tbody">{rows}</Box>
        </Box>
    )

    if (chartType === 'cumOi') {
        return renderTableBody(
            <SeriesOptionMetadataDisplay
                strike={''}
                viewKey={viewKey}
                callSeriesColor={colorPalette.callCumulative}
                putSeriesColor={colorPalette.putCumulative}
                callKey={'cum_oi_CE'}
                putKey={'cum_oi_PE'}
                callPut={cumCallPut ?? ['CE', 'PE']}
                chartType={chartType}
            />
        )
    }

    return renderTableBody(
        strikeRows.map((row) => (
            <MultiStrikeStrikeMetadataRow
                key={`${row.strike}-${row.callKey ?? ''}-${row.putKey ?? ''}`}
                viewKey={viewKey}
                strike={row.strike}
                callKey={row.callKey}
                putKey={row.putKey}
                callSeriesColor={row.callSeriesColor}
                putSeriesColor={row.putSeriesColor}
                callPut={row.callPut}
                chartType={chartType}
            />
        ))
    )
}

export default memo(MultiStrikeMetadataStrikeList)
